import { AdContainer } from '../ad-container/ad-container';
import {
  POPUP_INTERSTITIAL_DIMENSIONS,
  parseCreativeSizeMacro
} from 'common/services/ogury-callback/dimensions-utils';
import { findInWindowAncestors } from 'common/js/window-ancestors';

type OgAdContextWindow = Window & { OG_AD_CONTEXT?: OgAdContext };

type Dimensions = { width: number; height: number };

export type ContainerDimensionsGetter = {
  getContainerDimensions(
    aWindow: Window,
    options: ContainerDimensionOptions
  ): Dimensions;
};

/** How the container size was resolved (mirrors the old dom-root workaround order). */
export type ContainerDimensionSource =
  | 'sealed'
  | 'mraid'
  | 'macro'
  | 'frame'
  | 'window'
  | 'default';

/** Options carried from ad macros into resolution. */
export interface ContainerDimensionOptions {
  creative_size?: string;
  /** The real container size, measured server-side and sealed into the tag. */
  container_size?: string;
}

/** Dimensions plus the resolution state the old computer exposed via flags. */
export interface ResolvedContainerDimensions extends Dimensions {
  source: ContainerDimensionSource;
  /** Present when `creative_size` is a valid `WxH` macro pair. */
  creativeDimensions?: Dimensions;
}

export class ContainerDimensionRetriever implements ContainerDimensionsGetter {
  // Single source of truth shared with the layout engine.
  static readonly POP_UP_SIZE: Dimensions = POPUP_INTERSTITIAL_DIMENSIONS;
  static readonly DEFAULT_MRAID_SIZE: Dimensions = { width: 180, height: 135 };

  constructor(protected adContainer: AdContainer) {}

  /**
   * Same resolution order as `getContainerDimensions`, but also exposes the
   * state `container-dimensions-computer` used to expose (source, macro-parsed
   * creative size). Consumers use this to decide whether to abort
   * (unresolvable) or resize a too-small parent.
   */
  public resolve(
    aWindow: Window,
    options?: ContainerDimensionOptions
  ): ResolvedContainerDimensions {
    const creativeDimensions = parseCreativeSizeMacro(
      options ? options.creative_size : undefined
    );

    const state = {
      creativeDimensions
    };

    // 0. Sealed container size. In-app, no client-side geometry source is
    // correct, so a declared size wins over every measurement below.
    // The local bag comes first; a bundle routed through a VPAID host carries
    // its own macros, and an ancestor may belong to a different placement.
    const sealedDimensions =
      parseCreativeSizeMacro(options ? options.container_size : undefined) ??
      parseCreativeSizeMacro(readSealedContainerSize(aWindow));
    if (sealedDimensions) {
      return { ...sealedDimensions, source: 'sealed', ...state };
    }

    // 1. MRAID environment
    let size = this.adContainer.getMaxSize();
    if (size) {
      // Workaround for 0x0 or 180x135 thumbnails
      if (size.width === 0 || size.height === 0) {
        size = ContainerDimensionRetriever.DEFAULT_MRAID_SIZE;
      }
      return {
        width: size.width,
        height: size.height,
        source: 'mraid',
        ...state
      };
    }

    // 2. Macro-defined creative size
    if (creativeDimensions) {
      return { ...creativeDimensions, source: 'macro', ...state };
    }

    // 3. Iframe window size
    const frameElement: HTMLIFrameElement | null = (aWindow.frameElement as unknown) as HTMLIFrameElement | null;
    if (frameElement && frameElement.clientHeight && frameElement.clientWidth) {
      const w = frameElement.clientWidth;
      const h = frameElement.clientHeight;
      if (w > 1 && h > 1) {
        return { width: w, height: h, source: 'frame', ...state };
      }
    }
    if (aWindow.innerWidth > 1 && aWindow.innerHeight > 1) {
      return {
        width: aWindow.innerWidth,
        height: aWindow.innerHeight,
        source: 'window',
        ...state
      };
    }

    // 4. Default popup size
    return {
      ...ContainerDimensionRetriever.POP_UP_SIZE,
      source: 'default',
      ...state
    };
  }

  public getContainerDimensions(
    aWindow: Window,
    options: ContainerDimensionOptions
  ): Dimensions {
    const resolved = this.resolve(aWindow, options);
    return { width: resolved.width, height: resolved.height };
  }
}

/**
 * ms-bidder seals the measured container size into `OG_AD_CONTEXT`, emitted by
 * the same document that loads the tag. The walk covers the nested-frame cases:
 * an opaque or cross-origin boundary stops it, and resolution falls through.
 */
function readSealedContainerSize(aWindow: Window): string | undefined {
  return findInWindowAncestors<string>(
    aWindow,
    candidate => (candidate as OgAdContextWindow).OG_AD_CONTEXT?.container_size
  );
}
